import EditIcon from '@mui/icons-material/Edit';
import { Box, Chip, Theme } from '@mui/material';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import makeStyles from '@mui/styles/makeStyles';
import * as H from 'history';
import moment from 'moment';
import React from 'react';
import { generatePath } from 'react-router';
import {
  Link,
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams,
} from 'react-router-dom';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { IUserAtom, IUserModel, user } from '../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import AgentReportCharts from '../components/charts/AgentReportCharts';
import { AgentDropdown } from '../components/dropdowns/AgentsDropdown';
import { ThreadedSms } from '../components/sms/ThreadedSms';
import { CallHistory } from '../modules/History/Call';
import { SmsHistory } from '../modules/History/Sms';
import AgentSnapshot from '../subpages/AgentSnapshot';
import { makeNewAgentRoute } from './utils/makeNewRoute';

const useStyles = makeStyles((theme: Theme) => ({
  tabs: {
    borderBottom:
      theme.palette.mode === 'dark' ? '1px solid #515151' : '1px solid #e0e0e0',
    indicator: {
      backgroundColor: '#0098C7',
    },
  },
  tab: {
    textTransform: 'none',
    minWidth: 72,
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(4),
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    '&:hover': {
      color: '#40a9ff',
      opacity: 1,
    },
    '&[aria-selected=true]': {
      color: '#0098C7',
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  paper: {
    flexGrow: 1,
  },
  tabTitle: {
    display: 'flex',
    paddingTop: 15,
    justifyContent: 'space-between',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  line: {
    margin: '5px 20px',
    backgroundColor: '#2B7DE4',
  },
  chip: {
    marginLeft: theme.spacing(1),
  },
  powerlistContactBody: {
    '& [aria-label=statsToolbar]': {
      marginRight: theme.spacing(2),
      marginLeft: theme.spacing(2),
    },
  },
  editAgentBtn: {
    display: 'none',
  },
}));

interface IViewAgentReportingTabs {
  history: H.History;
  basePath: string;
}

export function ViewAgentReportingTabs({
  history,
  basePath = '/manage/agents/:id',
}: IViewAgentReportingTabs) {
  const { id } = useParams<{ id: string }>();
  const userLoadable = useRecoilValueLoadable(user);
  const userContents = userLoadable.contents as IUserAtom;
  // const [_, businessDataContents] = makeApiRequestCached(
  //   {
  //     endpoint: endpointEnum.GET_BUSINESS_DATA,
  //     payloadJsonString: JSON.stringify({
  //       call: methodNameEnum.GET_BUSINESS_DATA,
  //     }),
  //   },
  //   (loadable, cached) =>
  //     loadable.contents?.response?.data?.result?.[0] ?? cached.current
  // );
  // const businessData = businessDataContents.contents?.response?.data?.result[0];

  const setRefetchToggleForbusinessData = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_DATA)
  );

  const [
    businessUserCurrentCached,
    _businessUserCurrentLoadable,
  ] = makeApiRequestCached<IUserModel | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    },
    (loadable, cached) =>
      (loadable.contents?.response?.data?.result as IUserModel[])?.find(
        (user) => user.userid === +id
      ),
    undefined,
    [id]
  );

  if (basePath.startsWith('/my-profile/')) {
    /*
     * If the URL is my-profile then for security reasons we ensure the ID in the URL is loggedin users ID
     * If not we need to reset the id in agentUserId Atom and we redirect back to my profile with correct ID
     */
    const userId = userContents?.user?.userid;

    if (userId && id !== String(userId)) {
      history.push(generatePath(basePath, { id: userId }));
    }
  }
  const isEditPage =
    window.location.pathname === `/my-profile/${id}/edit` ||
    window.location.pathname === `/manage/agents/${id}/edit`;
  // const { canAccessPowerlistProduct } = useRecoilValueLoadable<IPermissions>(
  //   permissionsSelector
  // ).contents as IPermissions;

  const pages = {
    agentSnapshot: {
      pathname: `${basePath}/agent-snapshot`,
      showDuration: true,
      showCalendar: true,
    },
    editAgent: {
      pathname: `${basePath}/edit`,
      showDuration: true,
      showCalendar: true,
    },
    agentReporting: {
      pathname: `${basePath}/agent-reporting`,
      showDuration: true,
      showCalendar: true,
    },
    callHistory: {
      pathname: `${basePath}/call-history`,
      showCalendar: true,
    },
    smsHistory: {
      pathname: `${basePath}/sms-history`,
      showCalendar: true,
    },
    threadedSms: {
      pathname: `${basePath}/sms-history/threaded-sms/:userid/:outsidenumber`,
      showCalendar: true,
    },
    powerlists: {
      pathname: `${basePath}/powerlists`,
      showCalendar: false,
    },
  };
  const classes = useStyles();
  const location = useLocation();
  // const isEditPage = basePath.endsWith('/edit/');
  const myProfilePage = basePath.startsWith('/my-profile/');

  const userAtom = useRecoilValueLoadable(user);
  const loginUser = userAtom.contents?.user ?? {};
  return (
    <>
      <Paper className={classes.paper}>
        {location.pathname !== generatePath(basePath, { id }) &&
          location.pathname !== `${generatePath(basePath, { id })}/` && ( // on initial load prevent render since basePath tab undefined
            <Box display="flex" justifyContent="center" width="100%">
              <Tabs
                className={classes.tabs}
                value={
                  location?.pathname?.includes('powerlists')
                    ? generatePath(pages.powerlists.pathname, { id })
                    : location.pathname.split('/threaded-sms/')[0]
                } // ignore after threaded-smsore after threaded-sms
                indicatorColor="primary"
                textColor="primary"
                onChange={(event: React.ChangeEvent<any>, value: any) => {
                  setRefetchToggleForbusinessData({});
                }}
                variant="scrollable"
              >
                <Tab
                  className={classes.tab}
                  label="Agent Snapshot"
                  disableRipple
                  component={Link}
                  value={generatePath(pages.agentSnapshot.pathname, { id })}
                  to={{
                    pathname: generatePath(pages.agentSnapshot.pathname, {
                      id,
                    }),
                    search: location.search,
                  }}
                  data-pup="view-agent-tab-snapshot"
                />
                <Tab
                  className={classes.tab}
                  label="Call History"
                  disableRipple
                  component={Link}
                  value={generatePath(pages.callHistory.pathname, { id })}
                  to={{
                    pathname: generatePath(pages.callHistory.pathname, {
                      id,
                    }),
                    search: location.search,
                  }}
                  data-pup="view-agent-tab-call-history"
                />
                <Tab
                  className={classes.tab}
                  label={
                    window.location.pathname ===
                    `/my-profile/${loginUser.userid}/agent-reporting`
                      ? 'My Agent Summary'
                      : 'Reporting'
                  }
                  disableRipple
                  component={Link}
                  value={generatePath(pages.agentReporting.pathname, {
                    id,
                  })}
                  to={{
                    pathname: generatePath(pages.agentReporting.pathname, {
                      id,
                    }),
                    search: location.search,
                  }}
                  data-pup="view-agent-tab-reporting"
                />
                <Tab
                  className={classes.tab}
                  label="SMS History"
                  disableRipple
                  component={Link}
                  value={generatePath(pages.smsHistory.pathname, { id })}
                  to={{
                    pathname: generatePath(pages.smsHistory.pathname, {
                      id,
                    }),
                    search: location.search,
                  }}
                  data-pup="view-agent-tab-sms-history"
                />
              </Tabs>
            </Box>
          )}

        <header className={classes.tabTitle}>
          <div style={{ display: 'flex', alignItems: 'end' }}>
            {!myProfilePage && (
              <AgentDropdown
                userId={id}
                handleChange={(newValue) => {
                  if (!newValue?.userid) return;
                  history.push(
                    makeNewAgentRoute(id, newValue.userid, location.pathname)
                  );
                }}
              />
            )}
            <h2 style={{ position: 'relative', top: '3px' }}>
              {myProfilePage && (
                <>
                  {businessUserCurrentCached.current?.fname}{' '}
                  {businessUserCurrentCached.current?.lname}
                </>
              )}
              {myProfilePage ? (
                <Link
                  to={`/my-profile/${id}/edit#profile`}
                  className={`btn btn-outline-primary font-weight-bold
              ${isEditPage ? classes.editAgentBtn : ''}`}
                  style={{ marginLeft: 10, paddingLeft: 4 }}
                >
                  <EditIcon style={{ maxHeight: 15 }} /> Edit Agent
                </Link>
              ) : (
                <Link
                  to={`/manage/agents/${id}/edit#profile`}
                  className={`btn btn-outline-primary font-weight-bold
              ${isEditPage ? classes.editAgentBtn : ''}`}
                  style={{ marginLeft: 10, paddingLeft: 4 }}
                >
                  <EditIcon style={{ maxHeight: 15 }} /> Edit Agent
                </Link>
              )}
            </h2>
          </div>
          <aside>
            <span>
              {Boolean(businessUserCurrentCached.current?.created) && (
                <Chip
                  className={classes.chip}
                  label={moment(
                    businessUserCurrentCached.current?.created
                  ).format('YYYY-MM-DD')}
                  variant="outlined"
                  size="small"
                />
              )}
            </span>
            <span>
              {Boolean(businessUserCurrentCached.current?.userTier) && (
                <Chip
                  className={classes.chip}
                  label={
                    businessUserCurrentCached.current?.userTier === 'Enterprise'
                      ? 'Professional'
                      : businessUserCurrentCached.current?.userTier ===
                        'Enterprise Unlimited'
                      ? 'Professional Unlimited'
                      : businessUserCurrentCached.current?.userTier
                  }
                  variant="outlined"
                  size="small"
                />
              )}
            </span>
            <span>
              {Boolean(businessUserCurrentCached.current?.crmname) && (
                <Chip
                  className={classes.chip}
                  label={businessUserCurrentCached.current?.crmname}
                  variant="outlined"
                  size="small"
                />
              )}
            </span>
            <span>
              {Boolean(businessUserCurrentCached.current?.smsDID) && (
                <Chip
                  className={classes.chip}
                  label={businessUserCurrentCached.current?.smsDID}
                  variant="outlined"
                  size="small"
                />
              )}
            </span>
          </aside>
        </header>

        <Switch>
          <Route path="/my-profile/:userid/call-history/threaded-sms/:userid/:outsidenumber">
            {({ history, match }) => (
              <ThreadedSms
                show={match != null}
                onHide={() => {
                  history.push(
                    `/my-profile/${match?.params.userid}/call-history`
                  );
                }}
                userid={match?.params.userid}
                outsidenumber={match?.params.outsidenumber}
              />
            )}
          </Route>
          <Route path="/my-profile/:userid/sms-history/threaded-sms/:userid/:outsidenumber">
            {({ history, match }) => (
              <ThreadedSms
                show={match != null}
                onHide={() => {
                  history.push(
                    `/my-profile/${match?.params.userid}/sms-history`
                  );
                }}
                userid={match?.params.userid}
                outsidenumber={match?.params.outsidenumber}
              />
            )}
          </Route>
        </Switch>
        <Switch>
          <Redirect
            exact
            from={basePath}
            to={{
              pathname: pages.agentSnapshot.pathname,
              search: location.search,
            }}
          />
          <Route
            path={pages.agentSnapshot.pathname}
            render={(props) => <AgentSnapshot />}
          />
          <Route
            path={pages.agentReporting.pathname}
            render={(props) => <AgentReportCharts props={props} />}
          />
          <Route
            path={pages.callHistory.pathname}
            render={(props) => <CallHistory agentId={id} />}
          />
          <Route path={pages.threadedSms.pathname}>
            {({ history, match }) => (
              <ThreadedSms
                show={match != null}
                onHide={() => {
                  history.push(generatePath(pages.smsHistory.pathname, { id }));
                }}
                userid={match?.params.userid}
                outsidenumber={match?.params.outsidenumber}
              />
            )}
          </Route>
          <Route
            path={pages.smsHistory.pathname}
            render={(props) => <SmsHistory agentId={id} />}
          />
        </Switch>
      </Paper>
    </>
  );
}
